import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Send, Share2 } from "lucide-react";
import { api, errMsg, local } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { logError } from "@/lib/log";

const SIZE = 1080;
const cssVar = (n, fb) => getComputedStyle(document.documentElement).getPropertyValue(n).trim() || fb;
export const badgeSubtitle = (b) => (b.id.startsWith("plan_streak_") ? `${b.requirement} tough days in a row` : b.description);

const loadImg = (src) => new Promise((ok) => { const i = new Image(); i.onload = () => ok(i); i.onerror = () => ok(null); i.src = src; });

export const CARD_STYLES = [["soft", "Soft"], ["bold", "Bold"], ["minimal", "Minimal"]];
const STYLE_KEY = "badge_card_style";

const palette = (style) => {
  const t = { bg: cssVar("--mm-bg", "#F7F3FF"), surface: cssVar("--mm-surface", "#FFFFFF"), primary: cssVar("--mm-primary", "#7C5CBF"),
    text: cssVar("--mm-text", "#1a1a2e"), muted: cssVar("--mm-text-secondary", "#666") };
  if (style === "bold") return { bg: t.primary, card: null, ring: "#FFFFFF", ringAlpha: 0.2, title: "#FFFFFF", text: "#FFFFFF", muted: "rgba(255,255,255,.85)", icon: 200 };
  if (style === "minimal") return { bg: "#FFFFFF", card: null, outline: t.primary, ring: null, title: "#1a1a2e", text: "#555", muted: "#999", icon: 130 };
  return { bg: t.bg, card: t.surface, ring: t.primary, ringAlpha: 0.16, title: t.primary, text: t.text, muted: t.muted, icon: 200 };
};

const drawCard = async (b, style, message) => {
  const c = document.createElement("canvas");
  c.width = c.height = SIZE;
  const x = c.getContext("2d");
  const font = getComputedStyle(document.body).fontFamily || "sans-serif";
  const p = palette(style);
  x.fillStyle = p.bg; x.fillRect(0, 0, SIZE, SIZE);
  if (p.card) { x.fillStyle = p.card; x.beginPath(); x.roundRect(80, 80, SIZE - 160, SIZE - 160, 64); x.fill(); }
  if (p.outline) { x.strokeStyle = p.outline; x.lineWidth = 4; x.beginPath(); x.roundRect(90, 90, SIZE - 180, SIZE - 180, 48); x.stroke(); }
  if (p.ring) { x.globalAlpha = p.ringAlpha; x.fillStyle = p.ring; x.beginPath(); x.arc(SIZE / 2, 400, 190, 0, Math.PI * 2); x.fill(); x.globalAlpha = 1; }
  x.textAlign = "center"; x.textBaseline = "middle";
  x.font = `${p.icon}px ${font}`; x.fillText(b.icon, SIZE / 2, 410);
  x.fillStyle = p.title; x.font = `${style === "minimal" ? "600 64px" : "bold 72px"} ${font}`; x.fillText(b.name, SIZE / 2, 680, SIZE - 240);
  x.fillStyle = p.text; x.font = `40px ${font}`; x.fillText(badgeSubtitle(b), SIZE / 2, message ? 745 : 760, SIZE - 240);
  if (message) { x.font = `italic 36px ${font}`; x.fillText(`“${message}”`, SIZE / 2, 805, SIZE - 220); }
  const logo = await loadImg("/icon-192.png");
  x.fillStyle = p.muted; x.font = `600 34px ${font}`;
  if (logo) { x.drawImage(logo, SIZE / 2 - 150, 850, 56, 56); x.textAlign = "left"; x.fillText("Mindful Me", SIZE / 2 - 80, 879); }
  else x.fillText("Mindful Me", SIZE / 2, 879);
  return c.toDataURL("image/png");
};

const SendToSupporter = ({ badge, image, message }) => {
  const [sups, setSups] = useState(null);
  const [sid, setSid] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/supporters").then((r) => { const l = r.data.filter((s) => s.email); setSups(l); setSid(l[0]?.id || null); }).catch((e) => { logError("Supporters failed:", e); setSups([]); });
  }, []);
  const send = async () => {
    setBusy(true);
    try { toast.success((await api.post("/badges/share", { badge_id: badge.id, supporter_id: sid, image, message })).data.message); }
    catch (e) { toast.error(errMsg(e, "Could not send")); } finally { setBusy(false); }
  };
  if (!sups) return null;
  if (!sups.length) return <p className="text-xs text-mm-muted" data-testid="badge-share-no-supporters">Add a supporter with an email to send this.</p>;
  return (
    <div className="border-t pt-3" data-testid="badge-share-supporters">
      <div className="text-sm font-semibold mb-2">Send to a supporter</div>
      <div className="flex flex-wrap gap-1.5">
        {sups.map((s) => <button key={s.id} className={`mm-chip !py-1.5 ${sid === s.id ? "mm-chip-on" : ""}`} onClick={() => setSid(s.id)} data-testid={`badge-share-supporter-${s.id}`}>{s.name}</button>)}
      </div>
      <button className="mm-btn w-full mt-3 inline-flex items-center justify-center gap-2" disabled={busy || !sid} onClick={send} data-testid="badge-share-send">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send
      </button>
    </div>
  );
};

export const BadgeShareDialog = ({ badge, onClose }) => {
  const [img, setImg] = useState(null);
  const [style, setStyle] = useState(() => local.get(STYLE_KEY, "soft"));
  const [message, setMessage] = useState("");
  useEffect(() => {
    const t = setTimeout(() => drawCard(badge, style, message.trim()).then(setImg).catch((e) => logError("Badge card failed:", e)), 250);
    return () => clearTimeout(t);
  }, [badge, style, message]);
  const pickStyle = (s) => { setStyle(s); local.set(STYLE_KEY, s); };
  const file = async () => new File([await (await fetch(img)).blob()], `${badge.id}.png`, { type: "image/png" });
  const save = () => { const a = document.createElement("a"); a.href = img; a.download = `mindfulme-${badge.id}.png`; a.click(); toast.success("Image saved"); };
  const share = async () => {
    try {
      const f = await file();
      if (navigator.canShare?.({ files: [f] })) await navigator.share({ files: [f], title: badge.name, text: `I earned the ${badge.name} badge on Mindful Me 💜` });
      else { save(); toast.message("Sharing isn't available here, so we saved the image instead"); }
    } catch (e) { if (e.name !== "AbortError") toast.error("Could not open sharing"); }
  };
  const btn = "mm-btn-ghost !py-2 !px-4 text-sm inline-flex items-center justify-center gap-2 flex-1";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="badge-share-dialog">
        <DialogHeader>
          <DialogTitle>Share {badge.name}</DialogTitle>
          <DialogDescription>A card in your theme colors you can save or send.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1.5" data-testid="badge-style-picker">
          {CARD_STYLES.map(([id, l]) => <button key={id} className={`mm-chip !py-1.5 flex-1 justify-center ${style === id ? "mm-chip-on" : ""}`} onClick={() => pickStyle(id)} data-testid={`badge-style-${id}`}>{l}</button>)}
        </div>
        <input className="mm-input" maxLength={80} placeholder="Add a personal line (optional)" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="badge-share-message" />
        {img ? <img src={img} alt={`${badge.name} badge card`} className="w-full rounded-2xl border" data-testid="badge-share-image" /> : <Loader2 className="animate-spin mx-auto" />}
        <div className="flex gap-2">
          <button className={btn} disabled={!img} onClick={save} data-testid="badge-share-save"><Download size={15} /> Save image</button>
          <button className={btn} disabled={!img} onClick={share} data-testid="badge-share-native"><Share2 size={15} /> Share</button>
        </div>
        {img && <SendToSupporter badge={badge} image={img} message={message.trim()} />}
      </DialogContent>
    </Dialog>
  );
};
